import React, {
  useContext,
  useCallback,
  useState,
  createRef,
  useEffect,
} from 'react';
import debounce from 'lodash/debounce';
import { AppContext } from '@prisjakt/ui-app-context';
import { TextInput } from 'src/components/inputs';
import RouteContext from '../../../../routes/context';
import { ICON_SIZE } from '../../../../components/layout-buttons/constants';
import * as S from './styles';
import {
  ProductCollectionFilter,
  ProductCollectionNameSearchFilter,
} from '../../../../types';
import {
  ProductCollectionFilterContext,
  useTrackFilterEvent,
} from '../../filter-tracking';
import { TooltipPlacement } from 'src/components/tooltip-new';
import { IconClose, IconMagnifier } from '@prisjakt/ui/icon';

const debouncedDispatch = debounce(
  (query: string, dispatch: (params: any) => void, filters: any) => {
    dispatch({
      type: 'FILTER_NAME_SEARCH_UPDATE',
      query,
    });
  },
  800
);

export const getQueryFromFilters = (filters?: ProductCollectionFilter[]) => {
  const nameSearchFilter = filters?.find(
    (f: any): f is ProductCollectionNameSearchFilter =>
      'type' in f && f.type === 'name-search'
  );
  return nameSearchFilter ? nameSearchFilter.query : '';
};

export const useSearchQuery = () => {
  const { getState } = useContext(RouteContext);
  const { filters } = getState();
  const [query, setQuery] = useState(getQueryFromFilters(filters));
  useEffect(() => setQuery(getQueryFromFilters(filters)), [setQuery, filters]);
  return [query, setQuery] as const;
};

type SearchInputProps = {
  alwaysOpen?: boolean;
  inputWidth?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
  className?: string;
};

const SearchInput = ({
  alwaysOpen,
  inputWidth,
  inputRef: ref,
  className,
}: SearchInputProps) => {
  const { t } = useContext(AppContext);
  const { dispatch, getState } = useContext(RouteContext);
  const [query, setQuery] = useSearchQuery();
  const { trackFilterEventWithThrottle } = useTrackFilterEvent({
    type: 'product-name-search',
    title: 'product-name-search',
    property: 'product-name-search',
  });
  const hasQuery = query.length > 0;
  const [focused, setFocused] = useState(false);
  const isFocused = focused || alwaysOpen;
  const onFocus = useCallback(() => setFocused(true), [setFocused]);
  const onBlur = useCallback(() => setFocused(false), [setFocused]);
  const inputRef: React.RefObject<HTMLInputElement> = ref ?? createRef();
  const onOpen = useCallback(() => inputRef.current?.focus(), [inputRef]);
  const onUpdate = useCallback(
    (value: string) => {
      setQuery(value);
      if (value.length === 0 || value.length > 2) {
        trackFilterEventWithThrottle(value);
        debouncedDispatch(value, dispatch, getState().filters);
      }
    },
    [dispatch, getState, setQuery, trackFilterEventWithThrottle]
  );
  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onUpdate(e.target.value),
    [onUpdate]
  );
  const onClear = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.focus();
    }
    onUpdate('');
  }, [onUpdate, inputRef]);
  return (
    <S.SearchInput
      iconSize={ICON_SIZE}
      data-test="SearchInput"
      className={className}
    >
      <S.SearchTextWrapper
        expanded={isFocused || hasQuery}
        iconSize={ICON_SIZE}
        inputWidth={inputWidth}
      >
        <TextInput
          name="query"
          variant="rounded"
          value={query}
          inputRef={inputRef}
          placeholder={t('category', 'product-search.placeholder')}
          onChange={onInputChange}
          onBlur={onBlur}
          onFocus={onFocus}
          className="search-input-text"
        />
      </S.SearchTextWrapper>
      {!isFocused && !hasQuery && (
        <S.SearchInputIcon
          data-test="SearchInputIcon"
          positionAbsolute
          onClick={onOpen}
          iconSize={ICON_SIZE}
        >
          <S.Tooltip
            placement={TooltipPlacement.LEFT}
            title={t('category', 'product-search.placeholder')}
          >
            <IconMagnifier />
          </S.Tooltip>
        </S.SearchInputIcon>
      )}
      {hasQuery && (
        <S.SearchInputIcon
          data-test="SearchInputClearIcon"
          positionAbsolute
          onClick={onClear}
          iconSize={ICON_SIZE}
        >
          <IconClose size={16} />
        </S.SearchInputIcon>
      )}
    </S.SearchInput>
  );
};

const SearchInputHOC = (props: SearchInputProps) => (
  <ProductCollectionFilterContext.Provider
    value={{ filterLayout: 'product-name-search' }}
  >
    <SearchInput {...props} />
  </ProductCollectionFilterContext.Provider>
);

export default SearchInputHOC;
